import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download, BarChart3, Edit2 } from "lucide-react";
import { api, ApiClientError, Case } from "@/lib/api";
import { CaseDetailsPanel } from "@/components/CaseDetailsPanel";

// Frontend Case type with computed fields
interface CaseDisplay {
  id: string;
  case_name: string;
  agency: string;
  created_by: string;
  assigned_to: string[];
  total_urls: number;
  active_urls: number;
  removed_urls: number;
  avg_takedown_days: number;
  date_created: string;
  last_updated: string;
  ncii_types?: Record<string, number>;
}

const PAGE_SIZE = 50;

export function CaseList() {
  const { logout } = useAuth();
  const [cases, setCases] = useState<CaseDisplay[]>([]);
  const [rawCases, setRawCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.cases.list(PAGE_SIZE, undefined, true);
      // Handle paginated response
      const isPaginated =
        response && typeof response === "object" && "items" in response;
      const data = isPaginated
        ? (response as { items: Case[]; total: number; has_next_page: boolean })
            .items
        : (response as Case[]);
      const paginationData = isPaginated
        ? (response as { items: Case[]; total: number; has_next_page: boolean })
        : null;

      // Transform backend Case to frontend CaseDisplay format
      // TODO: Fetch URLs for each case to compute stats
      const transformedCases: CaseDisplay[] = await Promise.all(
        data.map(async (caseItem) => {
          let urls: any[] = [];
          try {
            urls = await api.cases.getUrls(caseItem.id);
          } catch {
            // If we can't fetch URLs, continue with empty array
          }
          const activeUrls = urls.filter((u) => u.status === "LIVE").length;
          const removedUrls = urls.filter(
            (u) =>
              u.status &&
              ["REMOVED", "BLOCKED", "DOMAIN DOWN", "DOMAIN SEIZED"].includes(
                u.status,
              ),
          ).length;

          return {
            id: caseItem.id,
            case_name: caseItem.name,
            agency: "N/A", // TODO: Fetch agency from case or user
            created_by: "N/A", // TODO: Fetch from case creator
            assigned_to: [], // TODO: Fetch from case_user_permissions
            total_urls: urls.length,
            active_urls: activeUrls,
            removed_urls: removedUrls,
            avg_takedown_days: 0, // TODO: Calculate from URL status changes
            date_created: caseItem.created_at,
            last_updated: caseItem.updated_at,
            ncii_types: undefined, // TODO: Calculate from evidence packages
          };
        }),
      );
      setCases(transformedCases);
      setRawCases(data);
      if (paginationData) {
        setHasMore(paginationData.has_next_page);
        setTotalCount(paginationData.total);
      } else {
        setHasMore(data.length === PAGE_SIZE);
        setTotalCount(data.length);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load cases");
        }
      } else {
        setError("Failed to load cases");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Cases table
  const columns: ColumnDef<CaseDisplay>[] = [
    {
      key: "case_name",
      header: "Case Name",
      render: (item) => (
        <div
          className="text-xs font-medium text-foreground cursor-pointer hover:text-primary"
          onClick={() => {
            const rawCase = rawCases.find((c) => c.id === item.id);
            if (rawCase) {
              setSelectedCase(rawCase);
              setIsPanelOpen(true);
            }
          }}
        >
          {item.case_name}
        </div>
      ),
      sortValue: (item) => item.case_name,
    },
    {
      key: "agency",
      header: "Agency",
      render: (item) => (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-800 border-blue-200 text-xs h-5 px-2"
        >
          {item.agency}
        </Badge>
      ),
      sortValue: (item) => item.agency,
    },
    {
      key: "created_by",
      header: "Created By",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.created_by}</div>
      ),
      sortValue: (item) => item.created_by,
    },
    {
      key: "assigned_to",
      header: "Assigned To",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.assigned_to.join(", ")}
        </div>
      ),
      sortValue: (item) => item.assigned_to.join(", "),
    },
    {
      key: "total_urls",
      header: "Total URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.total_urls}</div>
      ),
      sortValue: (item) => item.total_urls,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <span className="text-xs text-red-900">{item.active_urls}</span>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <span className="text-xs text-green-900">{item.removed_urls}</span>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "avg_takedown",
      header: "Avg Takedown",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_takedown_days.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_takedown_days,
    },
    {
      key: "date_created",
      header: "Date Created",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {new Date(item.date_created).toLocaleDateString()}
        </div>
      ),
      sortValue: (item) => new Date(item.date_created).getTime(),
    },
    {
      key: "last_updated",
      header: "Last Updated",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {new Date(item.last_updated).toLocaleDateString()}
        </div>
      ),
      sortValue: (item) => new Date(item.last_updated).getTime(),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3"
            onClick={() => {
              // TODO: Navigate to case details or open modal
              console.log("View case:", item.id);
            }}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => {
              // TODO: Open edit modal
              console.log("Edit case:", item.id);
            }}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    {
      label: "Agency",
      placeholder: "All Agencies",
      options: ["FBI", "DHS", "LAPD"],
    },
    { label: "Created By", placeholder: "All Users" },
    { label: "Status", placeholder: "All Statuses" },
  ];

  // Handle new case creation
  const handleNewCase = () => {
    // TODO: Implement new case modal or navigation
    console.log("Create new case");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export cases");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button size="sm" onClick={handleNewCase} className="h-9 px-4">
        <Plus className="h-4 w-4 mr-2" />
        New Case
      </Button>
    </>
  );

  return (
    <>
      <DataTable
        title="Case Management"
        subtitle="Optimize and track cases"
        columns={columns}
        data={cases}
        loading={loading}
        error={error}
        filters={filters}
        searchPlaceholder="Search cases..."
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        hasNextPage={hasMore}
        hasPreviousPage={currentPage > 1}
        currentPage={currentPage}
        itemsPerPage={PAGE_SIZE}
        actionButtons={actionButtons}
        totalCount={totalCount}
        entityName="cases"
      />
      <CaseDetailsPanel
        open={isPanelOpen}
        onOpenChange={setIsPanelOpen}
        caseData={selectedCase}
      />
    </>
  );
}
